import reactUpdate from 'immutability-helper';
import React from 'react';

import ColumnTypeDropdown, {SubmenuOption} from 'bulk-import/ColumnTypeDropdown';
import {getWarningMessage} from 'bulk-import/panes/ColumnsErrorBanner';
import {
  COLUMN_TYPE,
  ColumnMappingSource,
  IMPORT_TYPE,
  SelectedSchemaIds,
  SpreadsheetColumn,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import * as SpreadsheetBulkImportUtil from 'bulk-import/SpreadsheetBulkImportUtil';
import {SchemalessBulkImportSupportedOligoAwareTypes} from 'constants/RegistryConstants';
import {SAMPLE_TYPE_DISPLAY} from 'constants/SampleType';
import HelpButton from 'help/HelpButton';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

export interface SpreadsheetColumnsTableProps {
  setColumns: (columns: Array<SpreadsheetColumn> | undefined) => void;
  columnTypes: Array<COLUMN_TYPE>;
  columns?: Array<SpreadsheetColumn>;
  importType: IMPORT_TYPE;
  selectedSchemaIds: SelectedSchemaIds;
  tableColumnNames?: Array<string>;
  dataTableKeyFields?: Array<COLUMN_TYPE>;
  getValidSchemaFieldIds?: (columnIndex: number) => Array<string>;
  schemaFieldsToHideByColumnType?: Map<COLUMN_TYPE, Array<string>>;
  schemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes;
  additionalSubmenuOptionsByColumnType?: {[accessor: string]: Array<SubmenuOption>} | null;
}

class SpreadsheetColumnsTable extends React.Component<SpreadsheetColumnsTableProps> {
  private getMenuItemName = (columnType: COLUMN_TYPE): string =>
    SpreadsheetBulkImportUtil.getFieldName(
      columnType,
      this.props.selectedSchemaIds,
      this.props.importType,
      this.props.schemalessCreationEntityType
    );

  private getMenuItemHelpText = (columnType: COLUMN_TYPE): string =>
    SpreadsheetBulkImportUtil.getFieldHelpText(
      columnType,
      this.props.selectedSchemaIds,
      this.props.schemalessCreationEntityType
    );

  private getPopoverContent = (): JSX.Element => {
    const helpText = this.props.columnTypes.map((columnType: COLUMN_TYPE, i: number) => (
      <p key={i}>
        <strong>{this.getMenuItemName(columnType)}: </strong>
        {this.getMenuItemHelpText(columnType)}
      </p>
    ));

    return (
      <div>
        <p>This is how each cell in a column will be interpreted.</p>
        {helpText}
      </div>
    );
  };

  private handleChangeColumn = (
    columnIndex: number,
    newColumnType: COLUMN_TYPE,
    {
      newSchemaField,
      newTableColumnName,
      newSampleType,
      newCustomSyntaxId,
      newComplexPolymerComponent,
    }: {
      newSchemaField?: SchemaField;
      newTableColumnName?: string;
      newSampleType?: keyof typeof SAMPLE_TYPE_DISPLAY;
      newCustomSyntaxId?: string;
      newComplexPolymerComponent?: {name: string; schemaFieldId: string};
    } = {}
  ): void => {
    const newColumns: Array<SpreadsheetColumn> | undefined = reactUpdate(this.props.columns, {
      [columnIndex]: {
        type: {$set: newColumnType},
        schemaField: {$set: newSchemaField},
        tableColumnName: {$set: newTableColumnName},
        sampleType: {$set: newSampleType},
        customSyntaxId: {$set: newCustomSyntaxId},
        complexPolymerComponent: {$set: newComplexPolymerComponent},
        columnMappingSource: {$set: ColumnMappingSource.USER_SELECTED},
      },
    });
    this.props.setColumns(newColumns);
  };

  renderTableBodyContents = (): JSX.Element | Array<JSX.Element> => {
    // Check for empty columns array because the store initializes fileData.columns to []
    // during the SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE action, before the actual
    // column data is loaded and populated.
    if (!this.props.columns || this.props.columns.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={3}>
            <div className="u-small-padding">
              <HorizontalThrobber />
            </div>
          </td>
        </tr>
      );
    }
    return this.props.columns.map((column, i) => (
      <tr key={i}>
        <td className="bulk-import--column-text-column">
          <span className="bulk-import--table-text">{SpreadsheetBulkImportUtil.colNumToExcelId(i)}</span>
        </td>
        <td className="bulk-import--column-text-column">
          <span className="bulk-import--table-text">{column.name}</span>
        </td>
        <td className="bulk-import--column-type-column">
          <ColumnTypeDropdown
            className={getWarningMessage(column, this.props.importType) ? 'selectDropdown--warning' : null}
            column={column}
            columnIdx={i}
            columnTypes={this.props.columnTypes}
            dataTableKeyFields={this.props.dataTableKeyFields!}
            getMenuItemName={this.getMenuItemName}
            getValidSchemaFieldIds={this.props.getValidSchemaFieldIds!}
            schemaFieldsToHideByColumnType={this.props.schemaFieldsToHideByColumnType}
            importType={this.props.importType}
            onChangeColumn={this.handleChangeColumn}
            selectedSchemaIds={this.props.selectedSchemaIds}
            tableColumnNames={this.props.tableColumnNames!}
            additionalSubmenuOptionsByColumnType={this.props.additionalSubmenuOptionsByColumnType}
          />
        </td>
      </tr>
    ));
  };

  render(): JSX.Element {
    return (
      <table className="table">
        <thead>
          <tr>
            <th>Column</th>
            <th>Column name</th>
            <th>
              Column type
              <HelpButton
                renderContent={this.getPopoverContent}
                iconClassName="bulkImportModal-helpIcon u-tiny-spacing-left"
                placement={FloatingPlacement.BOTTOM}
              />
            </th>
          </tr>
        </thead>
        <tbody>{this.renderTableBodyContents()}</tbody>
      </table>
    );
  }
}

export default SpreadsheetColumnsTable;
